<template>
  <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
    <div>
      <div class="flex items-center gap-3">
        <h1 class="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
          Reportes & Balances de Gestión
        </h1>
        <span class="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-200">
          En Vivo
        </span>
      </div>
      <p class="text-sm text-gray-500 mt-1">
        Consulta balances financieros, ventas, compras y rotación de inventarios en una sola pantalla.
      </p>
    </div>

    <!-- Acciones y Exportación -->
    <div ref="dropdownRef" class="flex items-center gap-2 w-full sm:w-auto relative">
      <!-- Dropdown Exportar -->
      <div class="relative w-full sm:w-auto">
        <button
          type="button"
          class="w-full sm:w-auto px-4 py-2.5 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl font-bold text-xs shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-400"
          @click="isDropdownOpen = !isDropdownOpen"
        >
          <i class="fa fa-file-export text-orange-500" />
          <span>Exportar Informes</span>
          <i class="fa fa-chevron-down text-[10px] text-gray-400 transition-transform" :class="{ 'rotate-180': isDropdownOpen }" />
        </button>

        <!-- Menú Desplegable -->
        <div
          v-if="isDropdownOpen"
          class="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 text-xs animate-in fade-in zoom-in-95 duration-100"
        >
          <div class="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100 mb-1">
            Exportar en PDF
          </div>

          <a
            :href="getPdfUrl('all')"
            target="_blank"
            class="flex items-center gap-2.5 px-3 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors"
            @click="isDropdownOpen = false"
          >
            <i class="fa fa-file-pdf text-red-500 text-sm" />
            <div>
              <div class="font-bold">Reporte General Completo</div>
              <div class="text-[10px] text-gray-400">Todos los balances y rankings</div>
            </div>
          </a>

          <a
            :href="getPdfUrl('balance')"
            target="_blank"
            class="flex items-center gap-2.5 px-3 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors"
            @click="isDropdownOpen = false"
          >
            <i class="fa fa-chart-pie text-blue-500 text-sm" />
            <div>
              <div class="font-bold">Balance Financiero & P&L</div>
              <div class="text-[10px] text-gray-400">Estado de resultados y márgenes</div>
            </div>
          </a>

          <a
            :href="getPdfUrl('sales')"
            target="_blank"
            class="flex items-center gap-2.5 px-3 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors"
            @click="isDropdownOpen = false"
          >
            <i class="fa fa-shopping-cart text-green-500 text-sm" />
            <div>
              <div class="font-bold">Informe de Ventas</div>
              <div class="text-[10px] text-gray-400">Vendedores, top productos y pagos</div>
            </div>
          </a>

          <a
            :href="getPdfUrl('purchases')"
            target="_blank"
            class="flex items-center gap-2.5 px-3 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors"
            @click="isDropdownOpen = false"
          >
            <i class="fa fa-truck text-purple-500 text-sm" />
            <div>
              <div class="font-bold">Informe de Compras</div>
              <div class="text-[10px] text-gray-400">Proveedores y compras de stock</div>
            </div>
          </a>

          <a
            :href="getPdfUrl('inventory')"
            target="_blank"
            class="flex items-center gap-2.5 px-3 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors"
            @click="isDropdownOpen = false"
          >
            <i class="fa fa-boxes text-orange-500 text-sm" />
            <div>
              <div class="font-bold">Relación de Inventarios</div>
              <div class="text-[10px] text-gray-400">Más recargados vs más rápido se agotan</div>
            </div>
          </a>

          <div class="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-400 border-t border-b border-gray-100 my-1">
            Exportar en Excel
          </div>

          <a
            :href="getExcelUrl()"
            class="flex items-center gap-2.5 px-3 py-2 text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors"
            @click="isDropdownOpen = false"
          >
            <i class="fa fa-file-excel text-emerald-600 text-sm" />
            <div>
              <div class="font-bold">Descargar Excel (.xlsx)</div>
              <div class="text-[10px] text-gray-400">Datos consolidados del período</div>
            </div>
          </a>

          <div class="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-400 border-t border-b border-gray-100 my-1">
            Herramientas
          </div>

          <a
            href="/reports/product-catalog"
            target="_blank"
            class="flex items-center gap-2.5 px-3 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
            @click="isDropdownOpen = false"
          >
            <i class="fa fa-book text-blue-500 text-sm" />
            <div>
              <div class="font-bold">Catálogo Digital de Productos</div>
              <div class="text-[10px] text-gray-400">Descargar PDF de catálogo</div>
            </div>
          </a>
        </div>
      </div>

      <!-- Botón Catálogo Rápido -->
      <a
        v-if="accountId"
        :href="`/catalogo/${accountId}`"
        target="_blank"
        class="hidden sm:inline-flex items-center gap-2 px-3.5 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-xl font-bold text-xs transition-colors"
        title="Ver catálogo web público"
      >
        <i class="fa fa-external-link-alt text-xs" />
        <span>Catálogo Online</span>
      </a>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReportHeader',

  props: {
    filters: {
      type: Object,
      required: true,
    },
    accountId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      isDropdownOpen: false,
    }
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    handleClickOutside(e) {
      if (this.$refs.dropdownRef && !this.$refs.dropdownRef.contains(e.target)) {
        this.isDropdownOpen = false
      }
    },

    getPdfUrl(type = 'all') {
      const from = this.filters.date_from || ''
      const to = this.filters.date_to || ''
      return `/reports/export/pdf?type=${type}&date_from=${from}&date_to=${to}`
    },

    getExcelUrl() {
      const from = this.filters.date_from || ''
      const to = this.filters.date_to || ''
      return `/reports/export/excel?type=balance&date_from=${from}&date_to=${to}`
    },
  },
}
</script>
